import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCode, FaServer, FaCogs, FaUserFriends } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext';

const SkillCategory = ({ title, skills, icon, delay }) => {
  return (
    <motion.div
      className="bg-secondary/30 p-6 rounded-lg border border-primary/10 relative overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ 
        type: "spring",
        stiffness: 100,
        damping: 20,
        duration: 0.6,
        delay 
      }}
      whileHover={{ 
        backgroundColor: "rgba(34,197,94,0.4)",
        borderColor: "rgba(59,130,246,0.2)",
        scale: 1.01,
        transition: { duration: 0.2 }
      }}
    >
      <motion.div 
        className="flex items-center gap-3 mb-4"
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ 
          type: "spring",
          stiffness: 100,
          damping: 20,
          duration: 0.6,
          delay: delay + 0.1 
        }}
      >
        <motion.div
          whileHover={{ 
            rotate: 360,
            transition: { duration: 0.6, ease: "easeInOut" }
          }}
        >
        {icon}
        </motion.div>
        <h4 className="text-xl font-medium text-white">{title}</h4>
      </motion.div>
      
      <div className="flex flex-wrap gap-2">
        <AnimatePresence>
        {skills.map((skill, index) => (
          <motion.span
            key={index}
              className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm relative overflow-hidden"
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 20,
                duration: 0.4,
                delay: delay + (index * 0.03)
              }}
              whileHover={{ 
                y: -4,
                scale: 1.05,
                backgroundColor: "rgba(var(--primary-rgb), 0.2)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                transition: {
                  type: "spring",
                  stiffness: 400,
                  damping: 25
                }
              }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0"
                initial={{ x: "-100%" }}
                whileHover={{ x: "100%" }}
                transition={{ 
                  duration: 1.2,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <span className="relative z-10">{skill.name}</span>
          </motion.span>
        ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const Skills = () => {
  const { groupedSkills } = usePortfolio();
  // Get non-empty categories
  const categories = Object.entries(groupedSkills).filter(([_, skills]) => Array.isArray(skills) && skills.length > 0);

  if (!groupedSkills || categories.length === 0) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-6 bg-gray-700 w-1/3 rounded"></div>
        {[1, 2, 3, 4].map((index) => (
          <div key={index} className="space-y-4">
            <div className="h-5 bg-gray-700 w-1/4 rounded"></div>
            <div className="flex flex-wrap gap-2">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 w-16 rounded-full"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="py-8">
      <h3 className="text-2xl font-semibold text-white mb-8">
        My <span className="text-primary">Skills</span>
      </h3>
      {/* Responsive grid: 1 col mobile, 2-3 desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map(([category, skills]) => {
          const visibleSkills = skills.filter(skill => skill.visible);
          return (
            <div
              key={category}
              className="bg-secondary/30 rounded-lg shadow-md p-6 border border-primary/10 transition-transform duration-200 hover:scale-[1.03]"
            >
              {/* Category Title */}
              <h4 className="text-lg font-bold text-primary mb-4">{category}</h4>
              {/* Skill badges */}
              <div className="flex flex-wrap gap-2">
                {visibleSkills.map((skill) => (
                  <span
                    key={skill.id || skill.name}
                    className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {skill.name}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Skills; 